import { TFunction } from 'react-i18next';
import { mapValues as _mapValues } from 'lodash';
import { OptimizationErrors } from '@bringg/types';

import { RoutesPlannerTranslations } from './components';

export const getTranslations = (t: TFunction): RoutesPlannerTranslations => {
	return {
		runsTab: t('ROUTES_PLANNER.RUNS_TAB'),
		driversTab: t('ROUTES_PLANNER.DRIVERS_TAB'),
		vehiclesTab: t('ROUTES_PLANNER.VEHICLES_TAB'),
		breakSymbol: t('ROUTES_PLANNER.BREAK_SYMBOL'),
		errorMessages: {
			generalError: t('ROUTES_PLANNER.GENERAL_ERROR'),
			..._mapValues(OptimizationErrors, value => t(`ROUTE_OPTIMIZATION_INVALID_REQUEST_REASONS.${value.key}`))
		},
		timeWindowTranslations: {
			noEarlierThan: t('ROUTES_PLANNER.NO_EARLIER_THAN'),
			noLaterThan: t('ROUTES_PLANNER.NO_LATER_THAN')
		},
		notificationsTranslations: {
			outOfDeliveryBlockRange: t('ROUTES_PLANNER.OUT_OF_DELIVERY_BLOCK_RANGE'),
			outOfDeliveryBlockRangeNotice: t('ROUTES_PLANNER.OUT_OF_DELIVERY_BLOCK_RANGE_NOTICE'),
			itemDroppedBeforeCurrentTime: t('ROUTES_PLANNER.ORDER_DROPPED_PRIOR_TO_CURRENT_TIME'),
			collectionAfterDeliveryError: t('ROUTES_PLANNER.COLLECTION_AFTER_DELIVERY_ERROR'),
			deliveryBeforeCollectionError: t('ROUTES_PLANNER.DELIVERY_BEFORE_COLLECTION_ERROR'),
			moveLinkedOrder: t('ROUTES_PLANNER.MOVE_LINKED_ORDER'),
			unassignedLinkedTasks: t('ROUTES_PLANNER.UNASSIGN_LINKED_TASKS'),
			assignedLinkedTasks: t('ROUTES_PLANNER.ASSIGN_LINKED_TASKS'),
			order: t('ROUTES_PLANNER.NOTIFICATION.ORDER'),
			orders: t('ROUTES_PLANNER.NOTIFICATION.ORDERS'),
			outsidePromisedWindow: t('ROUTES_PLANNER.NOTIFICATION.OUTSIDE_TIME_WINDOW'),
			routeNotMatchSkills: t('ROUTES_PLANNER.NOTIFICATION.ROUTE_NOT_MATCH_SKILLS'),
			recommendChangeDriver: t('ROUTES_PLANNER.NOTIFICATION.RECOMMEND_CHANGE_DRIVER')
		},
		multiselectTranslations: {
			missingSkillsError: t('ROUTES_PLANNER.MULTISELECT_MISSING_SKILLS'),
			movedSuccessfully: t('ROUTES_PLANNER.MULTISELECT_SUCCESS_TO_MOVE'),
			dontSelectFromOtherRoute: t('ROUTES_PLANNER.MULTISELECT_DONT_SELECT_FROM_OTHER_ROUTE'),
			unassignSuccessfully: t('ROUTES_PLANNER.MULTISELECT_SUCCESS_TO_UNASSIGN'),
			failedToMove: t('ROUTES_PLANNER.MULTISELECT_FAILED_TO_MOVE'),
			moveToStart: t('ROUTES_PLANNER.MULTISELECT_MOVE_TO_START'),
			moveToEnd: t('ROUTES_PLANNER.MULTISELECT_MOVE_TO_END'),
			moveTo: t('ROUTES_PLANNER.MULTISELECT_MOVE_TO'),
			unassign: t('ROUTES_PLANNER.MULTISELECT_UNASSIGN'),
			selected: t('ROUTES_PLANNER.MULTISELECT_SELECTED')
		},
		tooltipTranslations: {
			deliver: t('ORDER.DELIVER'),
			driverName: t('ROUTES_PLANNER.TOOLTIP.DRIVER_NAME'),
			vehicleName: t('ROUTES_PLANNER.TOOLTIP.VEHICLE_NAME'),
			routeName: t('ROUTES_PLANNER.TOOLTIP.ROUTE_NAME'),
			collect: t('ORDER.COLLECT'),
			scheduledFor: t('ORDER.SCHEDULED_FOR'),
			totalVolume: t('ROUTES_PLANNER.TOOLTIP.TOTAL_VOLUME'),
			unassign: t('ROUTES_PLANNER.UNASSIGN'),
			moveTask: t('ROUTES_PLANNER.TOOLTIP.MOVE_TASK_HEADER'),
			taskInfo: t('ROUTES_PLANNER.TOOLTIP.TASK_INFO_HEADER'),
			taskStatus: {
				notAssigned: t('UTILS.ORDER_STATUS_NOT_ASSIGNED'),
				assigned: t('UTILS.ORDER_STATUS_ASSIGNED'),
				onTheWay: t('UTILS.ORDER_STATUS_ON_THE_WAY'),
				checkedIn: t('UTILS.ORDER_STATUS_CHECKED_IN'),
				done: t('UTILS.ORDER_STATUS_DONE'),
				late: t('UTILS.ORDER_STATUS_LATE'),
				accepted: t('UTILS.ORDER_STATUS_ACCEPTED'),
				cancelled: t('UTILS.ORDER_STATUS_CANCELLED'),
				rejected: t('UTILS.ORDER_STATUS_REJECTED'),
				unacknowledged: t('UTILS.ORDER_STATUS_UNACKNOWLEDGED'),
				pending: t('UTILS.ORDER_STATUS_PENDING')
			},
			resequenceTransltions: {
				priority: t('ROUTES_PLANNER.TOOLTIP.PRIORITY'),
				route: t('ROUTES_PLANNER.TOOLTIP.ROUTE'),
				apply: t('ROUTES_PLANNER.TOOLTIP.APPLY')
			},
			openOrderPage: t('ROUTES_PLANNER.TOOLTIP.OPEN_ORDER_PAGE'),
			teamsHome: t('ROUTES_PLANNER.TOOLTIP.TEAMS_HOME'),
			home: (name: string) => t('ROUTES_PLANNER.TOOLTIP.HOME', { name })
		},
		serviceAreasTranslations: {
			areaName: t('TEAM_MAP.AREA_NAME'),
			cancel: t('TEAM_MAP.CANCEL'),
			save: t('TEAM_MAP.SAVE'),
			deleteAreaTitle: t('TEAM_MAP.DELETE_AREA_TITLE'),
			delete: t('TEAM_MAP.DELETE')
		},
		routesTableTranslations: {
			header: t('ROUTES_PLANNER.TABLE.HEADER'),
			customerID: t('ROUTES_PLANER.TABLE.CUSTOMER_ID'),
			noCustomerName: t('ROUTES_PLANNER.TABLE.NO_CUSTOMER_NAME'),
			actions: {
				selectAll: t('ROUTES_PLANNER.TABLE.ACTIONS.SELECT_ALL'),
				collapseAll: t('ROUTES_PLANNER.TABLE.ACTIONS.COLLAPSE_ALL'),
				expandAll: t('ROUTES_PLANNER.TABLE.ACTIONS.EXPAND_ALL'),
				moveUnassignToTimeline: t('ROUTES_PLANNER.TABLE.ACTIONS.MOVE_UNASSIGN_TO_TIMELINE'),
				moveUnassignToTimelineIsLocked: t('ROUTES_PLANNER.TABLE.ACTIONS.MOVE_UNASSIGN_TO_TIMELINE_IS_LOCKED'),
				expandTable: t('ROUTES_PLANNER.TABLE.ACTIONS.EXPAND_TABLE'),
				shrinkTable: t('ROUTES_PLANNER.TABLE.ACTIONS.SHRINK_TABLE')
			},
			headers: {
				orderId: t('ROUTES_PLANNER.TABLE.HEADERS.ORDER_ID'),
				customerName: t('ROUTES_PLANNER.TABLE.HEADERS.CUSTOMER_NAME'),
				timeWindow: t('ROUTES_PLANNER.TABLE.HEADERS.TIME_WINDOW'),
				eta: t('ROUTES_PLANNER.TABLE.HEADERS.ETA'),
				totalWeight: t('ROUTES_PLANNER.TABLE.HEADERS.TOTAL_WEIGHT'),
				weight: t('ROUTES_PLANNER.TABLE.HEADERS.WEIGHT'),
				skills: t('ROUTES_PLANNER.TABLE.HEADERS.SKILLS'),
				unassigned: t('ROUTES_PLANNER.TABLE.HEADERS.UNASSIGNED'),
				handlingUnits: t('ROUTES_PLANNER.TABLE.HEADERS.HANDLING_UNITS'),
				miles: t('ORDER.MILES'),
				km: t('ORDER.KM'),
				totalDistance: t('ROUTES_PLANNER.TABLE.HEADERS.TOTAL_DISTANCE'),
				routeId: t('ROUTES_PLANNER.TABLE.HEADERS.ROUTE_ID'),
				driverName: t('ROUTES_PLANNER.TABLE.HEADERS.DRIVER_NAME'),
				priority: t('ROUTES_PLANNER.TABLE.HEADERS.PRIORITY'),
				vehicleName: t('ROUTES_PLANNER.TABLE.HEADERS.VEHICLE_NAME'),
				trailerName: t('ROUTES_PLANNER.TABLE.HEADERS.TRAILER_NAME'),
				tasksCount: t('ROUTES_PLANNER.TABLE.HEADERS.TASKS_COUNT'),
				plannedStartEndTimes: t('ROUTES_PLANNER.TABLE.HEADERS.PLANNED_START_END_TIMES'),
				unassignedDefaultReason: t('ROUTES_PLANNER.TABLE.HEADERS.UNASSIGNED_DEFAULT_REASON'),
				routesPlanner: t('DISPATCH_LIST.OPEN_ROUTE_PLANNER'),
				etos: t('ROUTES_PLANNER.TABLE.HEADERS.ETOS'),
				rank: t('ROUTES_PLANNER.TABLE.HEADERS.RANK'),
				totalPrice: t('ROUTES_PLANNER.TABLE.HEADERS.TOTAL_PRICE'),
				sla: t('ROUTES_PLANNER.TABLE.HEADERS.SLA'),
				orderType: t('ROUTES_PLANNER.TABLE.HEADERS.ORDER_TYPE'),
				duration: t('ROUTES_PLANNER.TABLE.HEADERS.DURATION'),
				runTimes: t('ROUTES_PLANNER.TABLE.HEADERS.RUN_TIMES'),
				driverRank: t('ROUTES_PLANNER.TABLE.HEADERS.DRIVER_RANK'),
				reason: t('ROUTES_PLANNER.TABLE.HEADERS.REASON')
			},
			summaryDetails: {
				totalOrders: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.ORDERS'),
				unassigned: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.UNASSIGNED'),
				assigned: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.ASSIGNED'),
				drivers: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.DRIVERS'),
				routes: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.ROUTES'),
				vehicles: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.VEHICLES'),
				columnsSelectionTitle: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.COLUMN_SELECTION_TITLE'),
				columnsSelectionEmpty: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.COLUMN_SELECTION_EMPTY'),
				columnsSelectionDefault: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.COLUMN_SELECTION_DEFAULT'),
				columnsSelectionSelected: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.COLUMN_SELECTION_SELECTED'),
				columnsSelectionUnSelected: t('ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.COLUMN_SELECTION_UNSELECTED'),
				columnsSelectionSearchPlaceholder: t(
					'ROUTES_PLANNER.TABLE.SUMMARY_DETAILS.COLUMN_SELECTION_SEARCH_PLACEHOLDER'
				)
			},
			groupHeaders: {
				unassignedOrders: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.UNASSIGNED_ORDERS'),
				pickup: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.PICKUP'),
				dropoff: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.DROPOFF'),
				both: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.BOTH'),
				pickupTooltip: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.PICKUP_TOOLTIP'),
				dropoffTooltip: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.DROPOFF_TOOLTIP'),
				bothTooltip: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.BOTH_TOOLTIP'),
				unassignedKey: t('ROUTES_PLANNER.TABLE.GROUP_HEADERS.UNASSIGNED_KEY')
			},
			emptyTable: {
				contactSupport: t('ROUTES_PLANNER.TABLE.CONTACT_SUPPORT'),
				noResults: t('ROUTES_PLANNER.TABLE.NO_RESULTS'),
				getInTouch: t('ROUTES_PLANNER.TABLE.GET_IN_TOUCH')
			},
			yes: t('ROUTES_PLANNER.TABLE.YES'),
			no: t('ROUTES_PLANNER.TABLE.NO')
		},
		actionsTranslations: {
			copy: t('TEAM_MAP.COPY_TO_CLIPBOARD'),
			optimizationUuid: t('ROUTES_PLANNER.OPTIMIZATION_UUID'),
			undoLastChange: t('ROUTES_PLANNER.UNDO_LAST_CHANGE')
		},
		consecutiveRoutesTranslations: {
			consecutiveRoute: t('ROUTES_PLANNER.CONSECUTIVE_ROUTE'),
			consecutiveRoutes: t('ROUTES_PLANNER.CONSECUTIVE_ROUTES')
		},
		dynamicAggregationsTranslations: {
			currentLoad: t('DYNAMIC_CAPACITY.CURRENT_LOAD_TEXT'),
			expectedPickup: t('DYNAMIC_CAPACITY.EXPECTED_PICKUP_TEXT')
		},
		kpisTranslations: {
			kpisByOriginal: t('ROUTES_PLANNER.KPIS.KPIS_BY_ORIGINAL'),
			kpisByManual: t('ROUTES_PLANNER.KPIS.KPIS_BY_MANUAL'),
			assigned: t('ROUTES_PLANNER.KPIS.ASSIGNED'),
			unassigned: t('ROUTES_PLANNER.KPIS.UNASSIGNED'),
			timeFairness: t('ROUTES_PLANNER.KPIS.TIME_FAIRNESS'),
			routesDuration: t('ROUTES_PLANNER.KPIS.TOTAL_ROUTES_DURATION'),
			routesDistance: t('ROUTES_PLANNER.KPIS.TOTAL_ROUTES_DISTANCE'),
			stopsPerHour: t('ROUTES_PLANNER.KPIS.STOPS_PER_HOUR'),
			intersections: t('ROUTES_PLANNER.KPIS.INTERSECTIONS')
		}
	};
};
